import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Prisma } from '@prisma/client';
import transactionsService from '../../api/services/transactions';

export type transactionsWithIncludes = Prisma.transactionsGetPayload<{
  include: {
    user: true;
    offer: {
      include: {
        user: true;
        currentRound: { select: { status: true; paymentMethod: true } };
      };
    };
    inspections: true;
    listings: true;
    addendum: { include: { addendumItems: true; upload: true } };
  };
}> & {
  contacts?: Pick<
    Prisma.transactionContactsGetPayload<object>,
    'id' | 'role' | 'name' | 'email' | 'phone' | 'company'
  >[];
  // The transactions endpoint also returns the materialized contract schedule.
  // Optional because not every producer of this type carries it (the admin
  // list views build partial rows), so consumers must handle it being absent.
  deadlines?: Prisma.transactionDeadlinesGetPayload<object>[];
  closingChecklist?: Prisma.closingChecklistGetPayload<object>[];
};

export type transactionsWithUserAndOffer = Prisma.transactionsGetPayload<{
  include: {
    user: true;
    offer: true;
    addendum: { include: { addendumItems: true; upload: true } };
  };
}>;

interface State {
  transactions: transactionsWithIncludes[];
  loading: boolean;
  error: string | null;
}

const initialState: State = {
  transactions: [],
  loading: false,
  error: null,
};

export const fetchTransactions = createAsyncThunk(
  'transactions/fetch',
  async (_params, { rejectWithValue }) => {
    try {
      const trans = await transactionsService.find();
      return trans;
    } catch (error) {
      return rejectWithValue('Failed to fetch trans');
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transactions/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await transactionsService.remove(id);
      return id;
    } catch (error) {
      return rejectWithValue('Failed to cancel transaction');
    }
  }
);

const transSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTransactions.fulfilled,
        (state, action: PayloadAction<transactionsWithIncludes[]>) => {
          state.loading = false;
          state.transactions = action.payload;
        }
      )
      .addCase(
        fetchTransactions.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      .addCase(
        deleteTransaction.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.transactions = state.transactions.filter(
            (t) => t.id !== action.payload
          );
        }
      );
  },
});

export default transSlice.reducer;
